import React, { useMemo, useCallback } from 'react';
import { Badge, Button, Select } from 'antd';
import { FaCheckCircle, FaCircleNotch, FaClone, FaExclamation, FaExclamationCircle } from 'react-icons/fa';
import { FiX, FiXCircle } from 'react-icons/fi';

// Define styles outside component to prevent recreation
const menuStyle = { maxHeight: 300, overflowY: 'auto' };
const suffixStyle = { marginLeft: 8, fontSize: '12px', color: '#909090' };
const countLabelStyle = { marginLeft: 8, fontSize: '12px', color: '#909090' };

const SelectDropdown = ({
    items = [],
    currentID,
    label = 'Select',
    suffix = '',
    disabled = false,
    onSelect,
    countLabel = '',
    className = '',
    approvedIcon = <FaCheckCircle color="var(--success-color)" />,
    variantIcon = <FaCircleNotch color='red' />
}) => {
    // Create a single click handler that uses the event target
    const handleMenuClick = useCallback(({ key }) => {
        onSelect(key);
    }, [onSelect]);

    // Memoize menu items without onClick functions
    const menuItems = useMemo(() => {
        return items.map(item => ({
            key: item.id,
            label: item.label || item.id,
            disabled: currentID === item.id,
        }));
    }, [items]);

    // Memoize menu config to prevent recreation
    const menuConfig = useMemo(() => ({
        items: menuItems,
        style: menuStyle,
        onClick: handleMenuClick
    }), [menuItems, handleMenuClick]);

    if (items.length === 0) {
        return (
            <Button type="default" disabled className={className}>
                {label}
                <span style={suffixStyle}>No Items</span>
            </Button>
        );
    }

    return (
        <Select
            variant='filled'
            value={currentID}
            onChange={onSelect}
            disabled={disabled}
            className={className}
            dropdownStyle={{ minWidth: 80 }}
        >
            {
                items.map(item => (
                    <Select.Option key={item.id} value={item.id} disabled={currentID === item.id} 
                    style={{
                            border: currentID === item.id 
                            ? '2px solid var(--primary-color)' : '',
                            backgroundColor: currentID === item.id ? 'var(--dull-dark-gray)' : '',
                            color: currentID === item.id ? 'var(--text-color)' : '',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                        }}>
                            <span style={{ minWidth: 28, display: 'inline-flex', marginRight: 8, alignItems: 'center', justifyContent: 'center' }}>
                                {item.index ? <Badge
                                    overflowCount={250}
                                    count={item.index}
                                    size='default'
                                    color='var(--dull-dark-gray2)'
                                    style={{
                                        borderColor: '#505050'
                                    }}
                                /> : null}
                            </span>
                            <span
                                style={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    color: item.is_dupe ? 'var(--primary-blue)' : 'inherit',
                                    fontWeight: item.is_dupe ? 'bold' : 'normal',
                                }}>
                                {(item.label ? item.label.slice(0, 7) : item.id.slice(0, 7))}...</span>
                            <div
                                style={{
                                    alignItems: 'center',
                                    gap: '4px',
                                    marginLeft: '8px',
                                    paddingRight: '5px',
                                }}>
                                {item.approved && <span>{approvedIcon}</span>}
                                {item.variant && <span>{variantIcon}</span>}
                                {item.dne && <span><FaExclamationCircle color='var(--error-color)' /></span>}
                                {item.rejected && <span><FiXCircle color='var(--error-color)' /></span>}
                            </div>
                        </div>
                    </Select.Option>
                ))
            }
        </Select >
    );
};

export default React.memo(SelectDropdown);
